import React from 'react'
import { faClockThreeThirtySolid, FaIcon } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import CheckboxWithLabelField from '../../contract/inputs/CheckboxWithLabelField'
import Radio from '@/modules/drivers/shared/Radio'
import { useCertificateStore } from '@/modules/drivers/stores/certificateStore'
import { NumberField } from '@/ui/form/controls/NumberField'
import { DateAndTimeField } from '@/ui/form/controls/DateAndTimeField'
import { TimeField } from '@/ui/form/controls/TimeField'

const CertificateRange = () => {
  const { data, setData } = useCertificateStore()
  const { tSafe } = useLocalize()

  // Backend (ActivityAttestationUpdater.UpdateActivities) only honors the
  // searchForNoData / searchForRestLongerThan filters for Rest (4),
  // TimeOff (5) and SickLeave (6). For Driving/Work/Availability (1-3) the
  // activity scan ignores these flags entirely, so disable + uncheck them
  // when the type is one of those.

  
  const searchFiltersAvailable = [4, 5, 6].includes(data?.attestationType)
  const searchDisabledTooltip = tSafe(
    'attestationForm.search-disabled-tooltip',
    'Niedostępne dla zaświadczeń typu Jazda / Praca / Dyspozycja',
  )

  // For Driving/Work/Availability (1-3) the backend inserts the matching
  // activity into the driver's calendar (shouldInsertActivity +
  // activityStartTime/activityEndTime) — same as the WPF
  // AttestationReportDialog (ShowHours section).
  const insertActivityAvailable = [1, 2, 3].includes(data?.attestationType)
  const insertActivityLabel =
    data?.attestationType === 3
      ? tSafe(
          'attestationForm.insert-availability-activity',
          'Wstaw czynność dyspozycji',
        )
      : tSafe(
          'attestationForm.insert-other-work-activity',
          'Wstaw czynność innej pracy',
        )
  // WPF parity (SelectedReportType setter): picking Rest/TimeOff/SickLeave
  // auto-checks "Wyszukaj okresy postoju", picking Driving/Work/Availability
  // clears both search filters.
  React.useEffect(() => {
    if (!data?.attestationType) {
      return
    }
    if (searchFiltersAvailable) {
      setData({
        ...data,
        searchForRestLongerThan: true,
        searchForNoData: false,
      })
    } else if (data?.searchForNoData || data?.searchForRestLongerThan) {
      setData({
        ...data,
        searchForNoData: false,
        searchForRestLongerThan: false,
      })
    }
  }, [data?.attestationType])

  const checkboxesForCertificate = [
    {
      field: 'searchForNoData',
      label: tSafe(
        'attestationForm.search-for-no-data',
        'Wyszukaj tylko okresy “brak danych” dłuższe niż',
      ),
      disabled: !searchFiltersAvailable,
      onChange: (checked: boolean) =>
        setData({
          ...data,
          searchForNoData: checked,
          ...(checked && { searchForRestLongerThan: false }),
        }),
      children: (
        <div className="tw-flex tw-items-center tw-gap-2">
          <NumberField
            inputSize="sm"
            placeholder="0"
            value={data?.searchForNoDataHour}
            setValue={(e) => setData({ ...data, searchForNoDataHour: e })}
            min={0}
            label="h"
          />
          <NumberField
            inputSize="sm"
            placeholder="0"
            value={data?.searchForNoDataMinute}
            setValue={(e) => setData({ ...data, searchForNoDataMinute: e })}
            min={0}
            max={59}
            label="min"
          />
        </div>
      ),
    },
    {
      field: 'searchForRestLongerThan',
      label: tSafe(
        'attestationForm.search-for-rest-longer-than',
        'Wyszukaj okresy postoju/ “brak danych” dłuższe niż',
      ),
      disabled: !searchFiltersAvailable,
      onChange: (checked: boolean) =>
        setData({
          ...data,
          searchForRestLongerThan: checked,
          ...(checked && { searchForNoData: false }),
        }),
      children: (
        <div className="tw-flex tw-items-center tw-gap-2">
          <NumberField
            inputSize="sm"
            placeholder="0"
            value={data?.searchForRestLongerThanHour}
            setValue={(e) =>
              setData({ ...data, searchForRestLongerThanHour: e })
            }
            min={0}
            label="h"
          />
          <NumberField
            inputSize="sm"
            placeholder="0"
            value={data?.searchForRestLongerThanMinute}
            setValue={(e) =>
              setData({ ...data, searchForRestLongerThanMinute: e })
            }
            min={0}
            max={59}
            label="min"
          />
        </div>
      ),
    },
  ]

  return (
    <div className="tw-flex tw-gap-4">
      <div className="tw-w-full tw-max-w-[200px] tw-space-y-2">
        <div className="tw-jusit tw-flex tw-flex-1 tw-items-center tw-gap-2 tw-font-medium tw-text-content-base">
          <FaIcon icon={faClockThreeThirtySolid} className="tw-max-w-[12px]" />
          <p className="tw-text-sm tw-font-semibold tw-text-content-base">
            Okres
          </p>
        </div>
      </div>
      <div className="tw-flex tw-w-full tw-flex-col tw-gap-2">
        <div className="tw-flex tw-justify-between tw-gap-2">
          <DateAndTimeField
            label="Data od"
            confirmSelection
            value={data?.startTime}
            setValue={(e) => setData({ ...data, startTime: e })}
          />
          <DateAndTimeField
            label="Data do"
            confirmSelection
            value={data?.endTime}
            setValue={(e) => setData({ ...data, endTime: e })}
          />
        </div>
        <div className="tw-space-y-4">
          {checkboxesForCertificate.map((c, i) => (
            <div
              key={i}
              title={c.disabled ? searchDisabledTooltip : undefined}
            >
              <CheckboxWithLabelField
                field={c.field as keyof typeof data}
                data={data}
                setData={setData}
                onChange={c.onChange}
                disabled={c.disabled}
                background={false}
                noPadding
                label={c.label}
              >
                {c.children}
              </CheckboxWithLabelField>
            </div>
          ))}
          {insertActivityAvailable && (
            <CheckboxWithLabelField
              field="shouldInsertActivity"
              data={data}
              setData={setData}
              onChange={(checked) =>
                setData({
                  ...data,
                  shouldInsertActivity: checked,
                  // WPF parity: unchecking forces the remaining time to be
                  // filled with a rest activity.
                  ...(checked
                    ? {}
                    : { fillInRemainingTimeWithRestActivity: true }),
                })
              }
              background={false}
              noPadding
              label={insertActivityLabel}
            >
              <div className="tw-flex tw-flex-col tw-gap-3">
                <div className="tw-flex tw-items-center tw-gap-2">
                  <p className="tw-text-sm tw-text-content-base">
                    {tSafe('common.from', 'od')}
                  </p>
                  <TimeField
                    inputSize="sm"
                    value={data?.activityStartTime}
                    setValue={(e) =>
                      setData({ ...data, activityStartTime: e ?? '' })
                    }
                  />
                  <p className="tw-text-sm tw-text-content-base">
                    {tSafe('common.to', 'do')}
                  </p>
                  <TimeField
                    inputSize="sm"
                    value={data?.activityEndTime}
                    setValue={(e) =>
                      setData({ ...data, activityEndTime: e ?? '' })
                    }
                  />
                </div>
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                  <p className="tw-text-sm tw-text-content-base">
                    {tSafe(
                      'attestationForm.fill-in-rest-with-activity',
                      'Wypełnij pozostały czas aktywnością',
                    )}
                    :
                  </p>
                  <div
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2"
                    onClick={() =>
                      setData({
                        ...data,
                        fillInRemainingTimeWithRestActivity: false,
                      })
                    }
                  >
                    <Radio
                      onClick={() =>
                        setData({
                          ...data,
                          fillInRemainingTimeWithRestActivity: false,
                        })
                      }
                      checked={
                        data?.fillInRemainingTimeWithRestActivity === false
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      {tSafe('attestationForm.NoData', 'braku danych')}
                    </p>
                  </div>
                  <div
                    className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2"
                    onClick={() =>
                      setData({
                        ...data,
                        fillInRemainingTimeWithRestActivity: true,
                      })
                    }
                  >
                    <Radio
                      onClick={() =>
                        setData({
                          ...data,
                          fillInRemainingTimeWithRestActivity: true,
                        })
                      }
                      checked={
                        data?.fillInRemainingTimeWithRestActivity === true
                      }
                    />
                    <p className="tw-text-sm tw-text-content-base">
                      {tSafe('attestationForm.Rest', 'odpoczynku')}
                    </p>
                  </div>
                </div>
              </div>
            </CheckboxWithLabelField>
          )}
        </div>
      </div>
    </div>
  )
}

export default CertificateRange
